import { Contact } from "@/api/types";
import { DocProducerDocument } from "@/views/docproducer";

// ---------------------------------------------------------------------------
// Output type
// ---------------------------------------------------------------------------

export type FormDocument = {
  /** Source DocProducer document id */
  id: string;
  /** docType of the source document, e.g. "order", "meetingMinutes", "docSuitabilityNew" */
  type: string;
  /** Human-readable name of the form/document, e.g. "Egnethetsvurdering" */
  formName: string;
  /** The form's own structured data, as entered — not flattened into PDF template strings */
  data: Record<string, unknown>;
};

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

/** Safe string coercion */
function cleanVal(val: unknown): string {
  if (val === null || val === undefined) return "";
  return String(val);
}

// ---------------------------------------------------------------------------
// Form name mapping
// ---------------------------------------------------------------------------

function getFormName(doc: DocProducerDocument, isJuridical: boolean): string {
  switch (doc.docType) {
    case "order": {
      const raw = doc.data as Record<string, unknown>;
      const newData = (raw?.newData ?? {}) as Record<string, unknown>;
      const existData = (raw?.existData ?? {}) as Record<string, unknown>;
      const portName = cleanVal(
        newData.dpPortfolioName || existData.dpPortfolioName || "Portefølje"
      );
      return `Ordre – ${portName}`;
    }
    case "meetingMinutes":
      return "Møtereferat";
    case "suitabilityStatement":
    case "docSuitabilityNew":
      return isJuridical
        ? "Egnethetsvurdering (juridisk)"
        : "Egnethetsvurdering";
    case "customerAgreement":
      return isJuridical ? "Kundeavtale (juridisk)" : "Kundeavtale";
    case "openCustomerInstruction":
      return "Åpen kundeinstruks";
    case "advisoryAgainst":
      return "Frarådning";
    case "uploaded":
      return doc.desc || "Opplastet dokument";
    case "documentFromSecurity": {
      const raw = doc.data as { documentType?: string };
      return raw.documentType || doc.desc || "Dokument fra verdipapir";
    }
    default:
      return doc.processName || doc.desc || doc.docType;
  }
}

// ---------------------------------------------------------------------------
// Form data extraction
// ---------------------------------------------------------------------------

/**
 * Normalizes a queued document's raw data into the plain object of form
 * variables it represents — e.g. an order's merged existData/newData, or a
 * suitability document's computed SuitStatementData fields.
 */
function extractFormData(doc: DocProducerDocument): Record<string, unknown> {
  switch (doc.docType) {
    case "uploaded": {
      const uploaded = doc.data as { file?: File; stream?: string };
      return {
        fileName: uploaded.file?.name ?? doc.desc ?? "",
        fileSize: uploaded.file?.size ?? null,
        fileType: uploaded.file?.type ?? null,
      };
    }
    case "documentFromSecurity": {
      const raw = doc.data as {
        fileName?: string;
        documentType?: string;
        docStamperData?: Record<string, unknown>;
      };
      return {
        fileName: raw.fileName ?? "",
        documentType: raw.documentType ?? "",
        ...(raw.docStamperData ?? {}),
      };
    }
    case "order": {
      const raw = doc.data as Record<string, unknown>;
      const existData = (raw?.existData ?? {}) as Record<string, unknown>;
      const newData = (raw?.newData ?? {}) as Record<string, unknown>;
      // newData reflects the form as currently filled in, so it wins over existData.
      return { ...existData, ...newData };
    }
    default: {
      const raw = doc.data as Record<string, unknown>;
      const newData = (raw?.newData ?? raw) as Record<string, unknown>;
      return { ...newData };
    }
  }
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

/**
 * Translates the queued DocProducer documents into a flat list of the
 * actual forms that were filled in, each carrying its own structured data.
 *
 * Unlike `buildDocumentProductionList`, this does not map documents onto PDF
 * templates or flatten values into template-placeholder strings — one entry
 * is returned per queued document, with its native form data intact.
 */
export function buildFormDocumentList(
  contact: Contact,
  documents: DocProducerDocument[]
): FormDocument[] {
  const isJuridical = contact.juridical.code !== "Person";

  return documents.map((doc) => ({
    id: doc.id,
    type: doc.docType,
    formName: getFormName(doc, isJuridical),
    data: extractFormData(doc),
  }));
}
